import { z } from 'zod'
import { asEnumType, asObjectType } from '@gqloom/zod'

// Filter operators enum
export const FilterOperator = {
  Is: 'is',
  IsNot: 'is_not',
  IsGreaterThan: 'is_greater_than',
  IsLessThan: 'is_less_than',
  IsBefore: 'is_before',
  IsAfter: 'is_after',
  IsBetween: 'is_between',
  Contains: 'contains',
  DoesNotContain: 'does_not_contain',
  In: 'in',
} as const

export type FilterOperatorType = (typeof FilterOperator)[keyof typeof FilterOperator]

// Base filter schema for common properties
const filterBaseSchema = z.object({
  logicalOperator: z
    .enum(['and', 'or'])
    .register(asEnumType, {
      name: 'LogicalOperator',
    })
    .default('and'),
  orderIndex: z.number().optional(),
})

// Date filter schema (for joinDate, lastVisitDate, etc.)
const dateFilterSchema = filterBaseSchema
  .extend({
    using: z.array(
      z.object({
        from: z.string().optional(),
        to: z.string().optional(),
        value: z.string().optional(),
        operator: z
          .enum([
            FilterOperator.Is,
            FilterOperator.IsBefore,
            FilterOperator.IsAfter,
            FilterOperator.IsBetween,
          ])
          .register(asEnumType, {
            name: 'DateFilterOperator',
          }),
      })
    ),
  })
  .superRefine(({ using }, ctx) => {
    if (!using) {
      return
    }

    for (let idx = 0; idx < using.length; idx++) {
      const filter = using[idx]
      if (!filter) {
        continue
      }

      if (filter.operator !== FilterOperator.IsBetween && filter.to) {
        ctx.addIssue({
          code: 'custom',
          path: ['using', idx.toString(), 'to'],
          message: "Value 'to' is not supported for this operator",
        })
      }

      if (filter.operator === FilterOperator.IsBetween && (!filter.from || !filter.to)) {
        ctx.addIssue({
          code: 'custom',
          path: ['using', idx.toString()],
          message: "Both 'from' and 'to' values are required for 'is_between' operator",
        })
      }
    }
  })
  .nullish()

const numericToDateFilterSchema = filterBaseSchema
  .extend({
    using: z.array(
      z.object({
        value: z.string().optional(),
        min: z.string().optional(),
        max: z.string().optional(),
        operator: z
          .enum([
            FilterOperator.Is,
            FilterOperator.IsNot,
            FilterOperator.IsGreaterThan,
            FilterOperator.IsLessThan,
            FilterOperator.IsBetween,
          ])
          .register(asEnumType, {
            name: 'NumericToDateFilterOperator',
          }),
      })
    ),
  })
  .nullish()

// Numeric filter schema (for age, days absent, etc.)
const numericFilterSchema = filterBaseSchema
  .extend({
    using: z.array(
      z.object({
        value: z.number().optional(),
        min: z.number().optional(),
        max: z.number().optional(),
        operator: z
          .enum([
            FilterOperator.Is,
            FilterOperator.IsNot,
            FilterOperator.IsGreaterThan,
            FilterOperator.IsLessThan,
            FilterOperator.IsBetween,
          ])
          .register(asEnumType, {
            name: 'NumericFilterOperator',
          }),
      })
    ),
  })
  .superRefine(({ using }, ctx) => {
    if (!using) {
      return
    }

    for (let idx = 0; idx < using.length; idx++) {
      const filter = using[idx]
      if (!filter) {
        continue
      }

      if (filter.operator === FilterOperator.IsBetween) {
        if (filter.min === undefined || filter.max === undefined) {
          ctx.addIssue({
            code: 'custom',
            path: ['using', idx.toString()],
            message: "Both 'min' and 'max' values are required for 'is_between' operator",
          })
        }
      } else {
        if (filter.value === undefined) {
          ctx.addIssue({
            code: 'custom',
            path: ['using', idx.toString(), 'value'],
            message: 'Value is required for this operator',
          })
        }
        if (filter.min !== undefined || filter.max !== undefined) {
          ctx.addIssue({
            code: 'custom',
            path: ['using', idx.toString()],
            message: "Min/max values are only supported for 'is_between' operator",
          })
        }
      }
    }
  })
  .nullish()

// Text filter schema (for names, email, etc.)
const textFilterSchema = filterBaseSchema
  .extend({
    using: z.array(
      z.object({
        value: z.string(),
        operator: z
          .enum([
            FilterOperator.Is,
            FilterOperator.IsNot,
            FilterOperator.Contains,
            FilterOperator.DoesNotContain,
          ])
          .register(asEnumType, {
            name: 'TextFilterOperator',
          }),
      })
    ),
  })
  .nullish()

// Select filter schema (for gender, status, etc.)
const selectFilterSchema = filterBaseSchema
  .extend({
    using: z.array(
      z.object({
        value: z.array(z.string()),
        operator: z.enum([FilterOperator.Is, FilterOperator.In]).register(asEnumType, {
          name: 'SelectFilterOperator',
        }),
      })
    ),
  })
  .nullish()

// Session type credit filter schema (for filtering clients by credit availability)
// Note: Not registered separately as a GraphQL type - it's used as a nested field inside clientFiltersSchema
// which handles the type registration. Registering it separately causes duplicate type errors.
export const sessionTypeCreditFilterSchema = z
  .object({
    siteSessionTypeId: z.uuid(),
    bookingDate: z.coerce.date().optional(), // For expiration check, defaults to now (coerce accepts ISO strings)
  })
  .nullish()

// Extract the condition types from the 'using' arrays
export type SelectCondition = {
  value: string[]
  operator: typeof FilterOperator.Is | typeof FilterOperator.In
}

export type TextCondition = {
  value: string
  operator:
    | typeof FilterOperator.Is
    | typeof FilterOperator.IsNot
    | typeof FilterOperator.Contains
    | typeof FilterOperator.DoesNotContain
}

export type NumericCondition = {
  value?: number
  min?: number
  max?: number
  operator:
    | typeof FilterOperator.Is
    | typeof FilterOperator.IsNot
    | typeof FilterOperator.IsGreaterThan
    | typeof FilterOperator.IsLessThan
    | typeof FilterOperator.IsBetween
}

export type NumericToDateCondition = {
  value?: string
  min?: string
  max?: string
  operator:
    | typeof FilterOperator.Is
    | typeof FilterOperator.IsNot
    | typeof FilterOperator.IsGreaterThan
    | typeof FilterOperator.IsLessThan
    | typeof FilterOperator.IsBetween
}

export type DateCondition = {
  from?: string
  to?: string
  value?: string
  operator:
    | typeof FilterOperator.Is
    | typeof FilterOperator.IsBefore
    | typeof FilterOperator.IsAfter
    | typeof FilterOperator.IsBetween
}

// Union type for all condition types
export type FilterCondition =
  | SelectCondition
  | TextCondition
  | NumericCondition
  | NumericToDateCondition
  | DateCondition

// Type for client values that can be evaluated against filters
export type ClientFilterValue = string | number | Date | null | undefined

// Type for filter values used in conditions
export type FilterValue = string | number | string[] | null | undefined

// Main client filters schema
export const clientFiltersSchema = z
  .object({
    // Profile filters
    firstName: textFilterSchema,
    lastName: textFilterSchema,
    fullName: textFilterSchema,
    email: textFilterSchema,
    phone: textFilterSchema,

    // Demographic filters
    gender: selectFilterSchema,
    ageRange: numericToDateFilterSchema,
    birthMonth: selectFilterSchema,

    // Status and dates
    clientStatus: selectFilterSchema,
    joinDate: dateFilterSchema,
    lastVisitDate: dateFilterSchema,
    lastPaymentDate: dateFilterSchema,
    lastAttendanceDate: dateFilterSchema,

    // Activity filters
    daysAbsent: numericToDateFilterSchema,
    noShowLateCancelAccrual: numericFilterSchema,
    attendanceMilestone: numericFilterSchema,
    // daysInactive: deprecated alias of daysAbsent (same shape, routed to the same
    // SQL helper in ClientsRepository). Kept here for read-tolerance of legacy
    // smart groups saved with this filter key; UI exposes only daysAbsent.
    daysInactive: numericToDateFilterSchema,
    sessionFrequency: selectFilterSchema,

    // Contract filters
    contractType: selectFilterSchema,
    contractStatus: selectFilterSchema,

    // Additional profile filters
    locationStatus: selectFilterSchema,
    isSuspended: selectFilterSchema,
    failedPaymentsCount: numericFilterSchema,

    // Communication preferences
    smsOptIn: selectFilterSchema,
    emailOptIn: selectFilterSchema,
    masterUnsubscribe: selectFilterSchema,

    // Credit availability filter (for booking flow)
    hasCreditsForSessionType: sessionTypeCreditFilterSchema,
  })
  .register(asObjectType, {
    name: 'ClientFilters',
    description: 'Client filters with specific validation per filter type',
  })

export type ClientFiltersSchema = z.infer<typeof clientFiltersSchema>
